import { useEffect, useRef } from "react";
import { useApolloClient } from "@apollo/client/react";
import { getTagDetails } from "src/queries/tags.graphql";
import { isTagType } from "src/util/tagType";
import type { GetTagDetailsQueryData } from "src/types/tagsGraphql";
import type { TagTypeNode } from "./buildTagTypeTree";
import {
  buildTagExpandPathIds,
  getImmediateParentTagIds,
  isActiveInTagTypeSection,
} from "./resolveTagExpandPath";

type TagLike = {
  id: string;
  type?: string | undefined;
  attributes?: Record<string, string | undefined> | undefined;
};

type UseAutoExpandActiveTagArgs = {
  tagTypeId: string;
  tagTypeName: string;
  activeTagId?: string | undefined;
  activeTag?: TagLike | null | undefined;
  tagTypesConfig: Record<string, unknown>;
  childrenLoaded: Record<string, boolean>;
  loadChildTags: (node: TagTypeNode) => Promise<unknown>;
  setToggledNodes: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  mergeActiveTag: (tag: TagLike) => void;
};

const fetchAncestorIdsForTag = async (
  client: ReturnType<typeof useApolloClient>,
  tag: TagLike,
  tagTypesConfig: Record<string, unknown>,
  tagTypeId: string
): Promise<string[]> => {
  const ancestors: string[] = [];
  const seen = new Set<string>();
  let current: TagLike | undefined = tag;

  while (current) {
    const parentIds = getImmediateParentTagIds(current, tagTypesConfig);
    const parentId = parentIds.find((id) => id && !seen.has(id));
    if (!parentId) {
      break;
    }
    seen.add(parentId);

    const { data } = await client.query<GetTagDetailsQueryData>({
      query: getTagDetails,
      variables: { tagId: parentId },
    });
    const parent = data?.results?.tags?.[0] as TagLike | undefined;
    if (!parent) {
      break;
    }

    ancestors.unshift(parentId);
    if (parent.type !== tagTypeId) {
      break;
    }
    current = parent;
  }

  return ancestors;
};

const useAutoExpandActiveTag = ({
  tagTypeId,
  tagTypeName,
  activeTagId,
  activeTag,
  tagTypesConfig,
  childrenLoaded,
  loadChildTags,
  setToggledNodes,
  mergeActiveTag,
}: UseAutoExpandActiveTagArgs) => {
  const client = useApolloClient();
  const childrenLoadedRef = useRef(childrenLoaded);
  childrenLoadedRef.current = childrenLoaded;
  const ancestorIdsRef = useRef<string[]>([]);
  const ancestorKeyRef = useRef<string | null>(null);

  useEffect(() => {
    ancestorIdsRef.current = [];
    ancestorKeyRef.current = null;
  }, [tagTypeId, activeTagId]);

  useEffect(() => {
    if (
      !activeTagId ||
      !isActiveInTagTypeSection(
        activeTagId,
        tagTypeId,
        tagTypesConfig,
        activeTag
      )
    ) {
      return;
    }

    let cancelled = false;

    const expandPath = async () => {
      const ancestorKey = `${tagTypeId}:${activeTagId}`;
      if (
        !isTagType(tagTypesConfig, activeTagId) &&
        activeTag &&
        ancestorKeyRef.current !== ancestorKey
      ) {
        mergeActiveTag(activeTag);
        ancestorIdsRef.current = await fetchAncestorIdsForTag(
          client,
          activeTag,
          tagTypesConfig,
          tagTypeId
        );
        ancestorKeyRef.current = ancestorKey;
      }

      if (cancelled) {
        return;
      }

      const pathIds = buildTagExpandPathIds(
        tagTypeId,
        activeTagId,
        tagTypesConfig,
        ancestorIdsRef.current
      );

      setToggledNodes((prev) => {
        const next = { ...prev };
        pathIds.forEach((nodeId) => {
          next[nodeId] = true;
        });
        return next;
      });

      for (const nodeId of pathIds) {
        if (cancelled || childrenLoadedRef.current[nodeId]) {
          continue;
        }

        await loadChildTags({
          id: nodeId,
          name: nodeId === tagTypeId ? tagTypeName : nodeId,
          isTagType: nodeId === tagTypeId,
        });
      }
    };

    expandPath().catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [
    activeTag,
    activeTagId,
    childrenLoaded,
    client,
    loadChildTags,
    mergeActiveTag,
    setToggledNodes,
    tagTypeId,
    tagTypeName,
    tagTypesConfig,
  ]);
};

export default useAutoExpandActiveTag;
